import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { useQuery } from "@tanstack/react-query";
import { ArrowDown } from "lucide-react";
import { SEO } from "../components/shared/SEO";
import { Reveal, ImageReveal } from "../components/shared/Reveal";
import { Counter } from "../components/shared/Counter";
import { ProjectCard } from "../components/shared/ProjectCard";
import { Pillars, ServiceCards, MethodTimeline, ProcessFlow, CTABand } from "../components/shared/Sections";
import { HERO, ABOUT, INVEST, IMAGES } from "../content/site";
import { BOOK_HOME } from "../content/book";
import { api, imgUrl } from "../lib/api";
import { useSettings } from "../context/SettingsContext";

const EASE = [0.16, 1, 0.3, 1];

const Hero = () => {
  const s = useSettings();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  return (
    <section ref={ref} data-testid="hero-section" className="relative min-h-[100svh] flex items-end bg-ink text-cream overflow-hidden">
      <motion.div style={{ y }} className="absolute inset-0">
        <motion.img
          src={imgUrl(s.hero_image) || IMAGES.hero}
          alt="Architettura contemporanea — Home Haven"
          initial={{ scale: 1.1 }}
          animate={{ scale: 1 }}
          transition={{ duration: 2.4, ease: EASE }}
          className="img-cover"
          fetchPriority="high"
        />
      </motion.div>
      <div className="absolute inset-0 bg-black/35" />
      <div className="absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-ink via-ink/50 to-transparent" />
      <div className="container-hh relative pb-24 md:pb-28 pt-40">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-8">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.3, ease: EASE }} className="eyebrow mb-6">{HERO.eyebrow}</motion.div>
            <motion.h1 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, delay: 0.45, ease: EASE }} className="h-display" data-testid="hero-headline">
              Sviluppiamo immobili. Trasformiamo spazi. <em className="italic font-light text-beige">Creiamo valore.</em>
            </motion.h1>
            <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.55, ease: EASE }} className="font-serif italic text-2xl md:text-3xl font-light text-cream/85 mt-6 max-w-2xl">Trasformiamo immobili in opportunità di valore.</motion.p>
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.7, ease: EASE }} className="flex flex-wrap gap-4 mt-12">
              <Link to="/progetti" data-testid="hero-cta-progetti" className="btn-light">Scopri i progetti</Link>
              <Link to="/investitori" data-testid="hero-cta-investi" className="btn-outline-light">Investi con noi</Link>
              <Link to="/proponi-immobile" data-testid="hero-cta-proponi" className="btn-link !text-cream !border-cream/40 self-center ml-2">Proponici un immobile</Link>
            </motion.div>
          </div>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.85, ease: EASE }} className="lg:col-span-4 lg:pl-8 lg:border-l border-white/15">
            <p className="text-sm md:text-base font-light leading-relaxed text-cream/80">{HERO.text1}</p>
            <p className="text-sm md:text-base font-light leading-relaxed text-cream/80 mt-4">{HERO.text2}</p>
          </motion.div>
        </div>
      </div>
      <motion.div animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 2.4 }} className="absolute bottom-8 right-8 md:right-12 text-cream/50 hidden md:block"><ArrowDown size={20} strokeWidth={1} /></motion.div>
    </section>
  );
};

export default function Home() {
  const { data: projects = [], isSuccess: projectsLoaded } = useQuery({ queryKey: ["projects"], queryFn: () => api.get("/projects").then((r) => r.data) });
  const { data: book } = useQuery({ queryKey: ["book"], queryFn: () => api.get("/book").then((r) => r.data) });
  const cities = new Set(projects.map((p) => p.city)).size;
  const sqm = projects.reduce((a, p) => a + (p.surface_sqm || 0), 0);
  const concluded = projects.filter((p) => p.status === "concluso").length;

  return (
    <>
      <SEO title="Sviluppo e Valorizzazione Immobiliare | Home Haven" description="Home Haven sviluppa, riqualifica e valorizza immobili a Messina e in Italia, trasformando opportunità immobiliari in progetti strutturati e misurabili." />
      <Hero />

      <section className="section bg-cream">
        <div className="container-hh grid grid-cols-1 lg:grid-cols-12 gap-14 lg:gap-8">
          <Reveal className="lg:col-span-6">
            <div className="eyebrow mb-6">Home Haven</div>
            <h2 className="h-section" data-testid="home-payoff">{HERO.payoff}</h2>
          </Reveal>
          <div className="lg:col-span-6 grid grid-cols-2 gap-10 lg:pl-12 lg:border-l border-sage/30">
            {projectsLoaded && projects.length > 0 && <Counter value={projects.length} label="Progetti sviluppati" testId="counter-projects" />}
            {projectsLoaded && concluded > 0 && <Counter value={concluded} label="Operazioni concluse" testId="counter-concluded" />}
            {projectsLoaded && sqm > 0 && <Counter value={sqm} suffix=" mq" label="Superficie trasformata" testId="counter-sqm" />}
            {projectsLoaded && cities > 0 && <Counter value={cities} label="Città" testId="counter-cities" />}
          </div>
        </div>
      </section>

      <section className="section bg-white">
        <div className="container-hh">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
            <div className="lg:col-span-5">
              <ImageReveal src={IMAGES.detail2} alt="Dettaglio architettonico" className="aspect-[4/5]" />
            </div>
            <div className="lg:col-span-7 lg:pt-12">
              <Reveal>
                <div className="eyebrow mb-6">Chi siamo</div>
                <h2 className="h-section">{ABOUT.title}</h2>
                <p className="lead mt-8 max-w-2xl">{ABOUT.paragraphs[0]}</p>
                <p className="lead mt-4 max-w-2xl">{ABOUT.paragraphs[1]}</p>
                <Link to="/chi-siamo" data-testid="home-about-link" className="btn-link mt-10">Scopri Home Haven</Link>
              </Reveal>
            </div>
          </div>
          <div className="mt-24 md:mt-32"><Pillars /></div>
        </div>
      </section>

      <section data-testid="home-book-section" className="section relative bg-navy text-cream overflow-hidden">
        <svg aria-hidden="true" className="pointer-events-none absolute inset-0 w-full h-full" viewBox="0 0 1200 600" preserveAspectRatio="none"><path d="M-20 420 C 250 380, 450 500, 700 380 S 1050 240, 1240 280" fill="none" stroke="#CBB279" strokeWidth="1" strokeDasharray="6 10" opacity="0.4" /><circle cx="700" cy="380" r="3" fill="#CBB279" opacity="0.7" /></svg>
        <div className="container-hh relative grid grid-cols-1 lg:grid-cols-12 gap-14 items-center">
          <Reveal className="lg:col-span-4 flex justify-center lg:justify-start">
            <Link to="/la-rotta-del-valore" className="block w-[200px] md:w-[240px] shadow-[20px_30px_60px_-15px_rgba(0,0,0,0.7)] md:[transform:rotateY(-12deg)]">
              {book?.cover_image ? <img src={imgUrl(book.cover_image)} alt="Copertina de La Rotta del Valore" loading="lazy" width={1200} height={1800} className="block w-full h-auto aspect-[2/3] object-cover bg-navy-soft" onError={(e) => { if (!e.currentTarget.dataset.fb) { e.currentTarget.dataset.fb = "1"; e.currentTarget.src = "/book-cover.jpg"; } }} /> : (
                <div className="w-full aspect-[2/3] bg-navy-soft border border-cream/20 flex flex-col justify-between p-6"><div className="text-[9px] uppercase tracking-[0.3em] text-gold">Giovanni Luca Costantino</div><div><div className="font-serif text-3xl font-light leading-[1.05]">La Rotta<br />del Valore</div><div className="w-8 h-px bg-gold mt-4" /></div><div className="text-[9px] uppercase tracking-[0.25em] text-cream/50">Il libro</div></div>
              )}
            </Link>
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-7 lg:col-start-6">
            <div className="eyebrow mb-6">Il libro — La Rotta del Valore</div>
            <h2 className="h-section">{BOOK_HOME.title}</h2>
            <p className="lead !text-cream/75 mt-8">{BOOK_HOME.text1}</p>
            <p className="lead !text-cream/75 mt-4">{BOOK_HOME.text2}</p>
            <div className="flex flex-wrap gap-4 mt-10">
              <Link to="/la-rotta-del-valore" data-testid="home-book-cta-scopri" className="btn-light">Scopri il libro</Link>
              <Link to="/la-rotta-del-valore#anteprima" data-testid="home-book-cta-anteprima" className="btn-outline-light">Leggi l'anteprima</Link>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="section bg-cream">
        <div className="container-hh">
          <Reveal className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-16 md:mb-20">
            <div className="lg:col-span-7">
              <div className="eyebrow mb-6">Cosa facciamo</div>
              <h2 className="h-section">Sviluppo immobiliare, dall'acquisizione alla valorizzazione.</h2>
            </div>
            <div className="lg:col-span-5 lg:pt-14"><p className="lead">Un approccio integrato che copre l'intera catena del valore: individuazione, analisi, trasformazione e strategia di uscita.</p></div>
          </Reveal>
          <ServiceCards />
        </div>
      </section>

      <section className="section bg-white">
        <div className="container-hh">
          <Reveal className="mb-20 md:mb-24">
            <div className="eyebrow mb-6">Il nostro metodo</div>
            <h2 className="h-section max-w-3xl">Sei fasi. Un unico obiettivo: creare valore.</h2>
          </Reveal>
          <MethodTimeline />
        </div>
      </section>

      <section className="section bg-cream" id="projects">
        <div className="container-hh">
          <Reveal className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-16 md:mb-20">
            <div>
              <div className="eyebrow mb-6">Projects</div>
              <h2 className="h-section">Operazioni immobiliari selezionate.</h2>
            </div>
            <Link to="/progetti" data-testid="home-projects-link" className="btn-link">Tutti i progetti</Link>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-x-16 md:gap-y-24">
            {projects.filter((p) => p.featured).slice(0, 4).map((p, i) => <ProjectCard key={p.id} p={p} index={i} />)}
          </div>
        </div>
      </section>

      <section className="section relative bg-ink text-cream grain overflow-hidden">
        <div className="container-hh relative">
          <Reveal className="max-w-3xl">
            <div className="eyebrow mb-6">Investitori</div>
            <h2 className="h-section">{INVEST.title}</h2>
            <p className="lead !text-cream/70 mt-8">{INVEST.paragraphs[0]} {INVEST.paragraphs[1]}</p>
          </Reveal>
          <div className="mt-20 md:mt-24 border-y border-white/10 py-12"><ProcessFlow /></div>
          <Reveal className="flex flex-wrap gap-4 mt-14">
            <Link to="/operazioni" data-testid="home-invest-cta-operazioni" className="btn-light">Scopri le operazioni aperte</Link>
            <Link to="/investitori" data-testid="home-invest-cta-parla" className="btn-outline-light">Parla con Home Haven</Link>
          </Reveal>
        </div>
      </section>

      <CTABand
        eyebrow="Proprietari e professionisti"
        title="Hai un immobile con potenziale? Parliamone."
        text="Valutiamo immobili da ristrutturare, frazionabili o con potenziale di valorizzazione."
        primary={{ to: "/proponi-immobile", label: "Proponi il tuo immobile", testId: "home-cta-proponi" }}
      />
    </>
  );
}
